import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import api, { formatApiError } from "@/lib/upr-api";

const ZONE_ORDER = ["pt", "eu", "row"];
const ZONE_HINTS = {
  pt: "Portugal — domestic shipping.",
  eu: "European Union — all 26 remaining EU-27 countries.",
  row: "Rest of World — anywhere outside PT & the EU.",
};

export default function ShippingAdmin() {
  const [rates, setRates] = useState(null);
  const [banner, setBanner] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/shipping/rates");
      setRates(data.rates);
      setBanner(data.promo_banner || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not load shipping rates.");
    }
  };

  useEffect(() => { load(); }, []);

  const patch = (zone, field, value) => {
    setRates((r) => ({ ...r, [zone]: { ...r[zone], [field]: value } }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = ZONE_ORDER.reduce((acc, k) => {
        const z = rates[k] || {};
        acc[k] = {
          label: (z.label || "").trim(),
          price: Number(z.price) || 0,
          free_over: Number(z.free_over) || 0,
        };
        return acc;
      }, {});
      payload.promo_banner = (banner || "").trim();
      const { data } = await api.put("/shipping/rates", payload);
      setRates(data.rates);
      setBanner(data.promo_banner || "");
      toast.success("Shipping rates saved.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not save.");
    } finally {
      setSaving(false);
    }
  };

  if (!rates) return <div className="text-sm" style={{ color: "var(--upr-muted)" }}>Loading shipping rates…</div>;

  return (
    <section data-testid="shipping-admin">
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h2 className="font-heading font-extrabold text-2xl tracking-tight">Shipping rates</h2>
          <p className="text-sm mt-1 max-w-xl" style={{ color: "var(--upr-muted)" }}>
            You ship from Portugal. Set the flat rate for each zone (in EUR). Optionally set a
            <em> Free over</em> threshold to make shipping free once the cart subtotal reaches that amount.
            Set it to <strong>0</strong> to always charge the flat rate.
          </p>
        </div>
        <button className="upr-btn" onClick={save} disabled={saving} data-testid="ship-save">
          <Save size={14} /> {saving ? "Saving…" : "Save rates"}
        </button>
      </div>

      <div className="space-y-6">
        <div
          className="p-5"
          style={{ border: "1px solid var(--upr-border)", background: "var(--upr-surface)" }}
          data-testid="ship-promo-banner-wrap"
        >
          <div className="upr-eyebrow mb-1">Promo banner (optional)</div>
          <p className="text-xs mb-3" style={{ color: "var(--upr-muted)" }}>
            Shown as a banner across the top of the Merch page. Leave empty to hide it.
            Example: <em>Free EU shipping on orders over €50 · Limited-run vinyl restocks this week.</em>
          </p>
          <input
            type="text"
            value={banner}
            onChange={(e) => setBanner(e.target.value)}
            placeholder="e.g. Free EU shipping on orders over €50"
            maxLength={240}
            data-testid="ship-promo-banner"
            className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)] text-sm"
            style={{ borderColor: "var(--upr-border)" }}
          />
          <div className="text-xs mt-1" style={{ color: "var(--upr-muted)" }}>
            {banner.length}/240
          </div>
        </div>

        {ZONE_ORDER.map((key) => {
          const zone = rates[key] || {};
          return (
            <div
              key={key}
              className="p-5"
              style={{ border: "1px solid var(--upr-border)", background: "var(--upr-surface)" }}
              data-testid={`ship-zone-${key}`}
            >
              <div className="upr-eyebrow mb-1">Zone · {key.toUpperCase()}</div>
              <p className="text-xs mb-4" style={{ color: "var(--upr-muted)" }}>{ZONE_HINTS[key]}</p>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="upr-eyebrow block mb-1" htmlFor={`ship-${key}-label`}>Label</label>
                  <input
                    id={`ship-${key}-label`}
                    type="text"
                    value={zone.label || ""}
                    onChange={(e) => patch(key, "label", e.target.value)}
                    data-testid={`ship-${key}-label`}
                    className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)] text-sm"
                    style={{ borderColor: "var(--upr-border)" }}
                  />
                </div>
                <div>
                  <label className="upr-eyebrow block mb-1" htmlFor={`ship-${key}-price`}>Price (EUR)</label>
                  <input
                    id={`ship-${key}-price`}
                    type="number"
                    min="0"
                    step="0.5"
                    value={zone.price ?? 0}
                    onChange={(e) => patch(key, "price", e.target.value)}
                    data-testid={`ship-${key}-price`}
                    className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)] text-sm"
                    style={{ borderColor: "var(--upr-border)" }}
                  />
                </div>
                <div>
                  <label className="upr-eyebrow block mb-1" htmlFor={`ship-${key}-freeover`}>Free over (EUR, 0 = off)</label>
                  <input
                    id={`ship-${key}-freeover`}
                    type="number"
                    min="0"
                    step="5"
                    value={zone.free_over ?? 0}
                    onChange={(e) => patch(key, "free_over", e.target.value)}
                    data-testid={`ship-${key}-freeover`}
                    className="w-full p-2 bg-transparent border focus:outline-none focus:border-[color:var(--upr-primary)] text-sm"
                    style={{ borderColor: "var(--upr-border)" }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-xs mt-6" style={{ color: "var(--upr-muted)" }}>
        Digital-only carts (all items with category <code>digital</code>) skip shipping entirely.
        Country → zone mapping is fixed server-side so customers can&apos;t spoof a cheaper zone.
      </p>
    </section>
  );
}
